// src/pages/Profile.jsx
import React, { useEffect, useState, useContext } from "react";
import api from "../api/api";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function Profile() {
  const { user, logout } = useContext(AuthContext);
  const [form, setForm] = useState({ name: "", email: "", password: "", avatar: "" });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // ✅ Fetch user details
  useEffect(() => {
    const fetchUser = async () => {
      if (!user?._id) return;
      try {
        const { data } = await api.get(`/auth/user/${user._id}`);
        setForm({
          name: data.user.name,
          email: data.user.email,
          password: "",
          avatar: data.user.avatar || "", // agar avatar saved hai to
        });
      } catch (err) {
        setMessage("❌ Error fetching user details");
      }
    };
    fetchUser();
  }, [user]);

  // ✅ Update handler
  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put("/auth/user/update", form);
      setMessage("✅ Profile updated successfully!");
    } catch (err) {
      setMessage(err.response?.data?.message || "❌ Error updating profile");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Delete handler
  const handleDelete = async () => {
    if (
      !window.confirm(
        "⚠️ Are you sure you want to delete your account? This action cannot be undone."
      )
    )
      return;
    try {
      await api.delete("/auth/user/delete");
      logout();
      navigate("/register");
    } catch (err) {
      setMessage("❌ Error deleting account");
    }
  };

  // ✅ Logout handler
  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
      logout();
      navigate("/login");
    } catch (err) {
      console.error("Logout error", err);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-100/40 via-purple-100/30 to-pink-100/30 backdrop-blur-md px-4 z-50">
      <div className="bg-white/20 backdrop-blur-xl shadow-2xl rounded-2xl p-8 w-full max-w-md border border-white/30">
        
        {/* Avatar Section */}
        <div className="flex flex-col items-center">
          <img
            src={form.avatar || "https://i.pravatar.cc/120"} // Default Avatar
            alt="User Avatar"
            className="w-24 h-24 rounded-full border-4 border-white shadow-md object-cover"
          />
          <h2 className="text-2xl font-bold text-gray-900 mt-3">{form.name || "User"}</h2>
          <p className="text-gray-700 mb-6">{form.email}</p>
        </div>

        {/* Messages */}
        {message && (
          <p
            className={`text-center mb-4 font-medium ${
              message.startsWith("✅") ? "text-green-600" : "text-red-600"
            }`}
          >
            {message}
          </p>
        )}

        {/* Update Form */}
        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Full Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-white/40 bg-white/30 rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-white/40 bg-white/30 rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Avatar URL</label>
            <input
              type="text"
              value={form.avatar}
              onChange={(e) => setForm({ ...form, avatar: e.target.value })}
              className="w-full border border-white/40 bg-white/30 rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              placeholder="https://example.com/photo.jpg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">
              New Password (optional)
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full border border-white/40 bg-white/30 rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600/80 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50"
          >
            {loading ? "Updating..." : "Update Profile"}
          </button>
        </form>

        {/* Action Buttons */}
        <div className="mt-6 flex flex-col gap-3">
          <button
            onClick={handleDelete}
            className="w-full bg-red-600/80 text-white py-2 rounded-lg font-semibold hover:bg-red-700 transition"
          >
            Delete Account
          </button>
          <button
            onClick={handleLogout}
            className="w-full bg-gray-700/80 text-white py-2 rounded-lg font-semibold hover:bg-gray-800 transition"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}
